import { type MouseEvent, memo, type ReactNode, useCallback, useState } from 'react';
import { flushSync } from 'react-dom';

import { type ClipStatus } from '@air/api/types';
import { ContextMenu, renderContextMenuItems } from '@air/primitive-context-menu';
import { cn } from '@air/utils-tailwind';

import { GalleryCardSelectionButton } from '~/components/Gallery/GalleryCard/GalleryCardSelectionButton';
import { type GetSelectionMenuOptionsType } from '~/components/Gallery/types';
import { type GetItemMenuOptions } from '~/constants/MenuOptions';
import { type UseSelectableCardReturnType, useSelectableCard } from '~/hooks/useSelectableCard';
import { useSelectionAwareContextMenuOptions } from '~/hooks/useSelectionAwareContextMenuOptions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';
import { isMobileAgent } from '~/utils/PlatformHelpers';

export type GalleryCardProps<Item extends SelectableGalleryItem> = {
  item: Item;
  testId: string;
  title: string;
  getItemMenuOptions: GetItemMenuOptions;
  getSelectionMenuOptions: GetSelectionMenuOptionsType;
  isSelectable: boolean;
  onClick?: () => void;
  onMouseLeave?: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseMove?: (event: MouseEvent<HTMLDivElement>) => void;
  children: (
    props: UseSelectableCardReturnType & { setIsMenuOpen: (isOpen: boolean) => void; isMenuOpen: boolean },
  ) => ReactNode;
  getButtonClassName?: (props: { isSelected: boolean }) => string;
  className?: string;
  'data-clip-state'?: ClipStatus;
  'data-asset-id'?: string;
  'data-file-id'?: string;
};

const _GalleryCard = <Item extends SelectableGalleryItem>({
  item,
  testId,
  title,
  getItemMenuOptions,
  getSelectionMenuOptions,
  isSelectable = true,
  onClick,
  onMouseLeave,
  onMouseMove,
  children,
  getButtonClassName,
  className,
  'data-clip-state': dataClipState,
  'data-asset-id': dataAssetId,
  'data-file-id': dataFileId,
}: GalleryCardProps<Item>) => {
  const {
    onBlur,
    onFocus,
    handleHover,
    isHovering,
    handleItemClick,
    handleItemDoubleClick,
    handleUnhover,
    confirmItemSelection,
    preSelectItem,
    itemRef,
    isSelected,
    isHighlighted,
  } = useSelectableCard({
    item,
    onClick,
    isSelectable,
  });

  /** Sub Menu */
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const contextMenuOptions = getItemMenuOptions({ trackLocation: 'gallery-item-context-menu' });

  /** Context Menu */
  const { contentClassName, options } = useSelectionAwareContextMenuOptions({
    contextMenuOptions,
    id: item.id,
    selectionMenuOptions: getSelectionMenuOptions('gallery-multi-select-context-menu'),
  });

  const handleMouseEnter = useCallback(() => {
    // Skip hover state on mobile devices to prevent overlay showing before click
    if (!isMobileAgent) {
      flushSync(() => {
        handleHover();
      });
    }
  }, [handleHover]);

  const handleMouseMove = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      handleMouseEnter();
      onMouseMove?.(event);
    },
    [handleMouseEnter, onMouseMove],
  );

  const handleMouseLeave = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      // Skip unhover on mobile since hover is never set
      if (!isMobileAgent) {
        handleUnhover();
      }
      onMouseLeave?.(event);
    },
    [handleUnhover, onMouseLeave],
  );

  /**
   * If the menu is open, we want to keep the hover state active
   */
  const _isHovering = isHovering || isMenuOpen;

  return (
    <ContextMenu
      className={contentClassName}
      trigger={
        // biome-ignore lint/a11y/noStaticElementInteractions: mouse and focus handlers track card hover; interactive controls remain nested inside
        <div
          data-clip-state={dataClipState}
          data-testid={testId}
          data-asset-title={title}
          data-asset-id={dataAssetId}
          data-file-id={dataFileId}
          data-test-isselected={isSelected}
          className={cn('flex h-full flex-col', className)}
          onBlur={onBlur}
          onFocus={onFocus}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleMouseMove}
          ref={itemRef}
        >
          <GalleryCardSelectionButton
            confirmItemSelection={confirmItemSelection}
            isSelectable={isSelectable}
            onClick={onClick}
            onDoubleClick={handleItemDoubleClick}
            preSelectItem={preSelectItem}
            isHovering={_isHovering}
            isHighlighted={isHighlighted || isSelected}
            className={getButtonClassName?.({ isSelected })}
            data-selection-button
          />
          {children({
            setIsMenuOpen,
            isMenuOpen,
            onBlur,
            onFocus,
            handleHover,
            isHovering: _isHovering,
            handleItemClick,
            handleItemDoubleClick,
            handleUnhover,
            confirmItemSelection,
            preSelectItem,
            itemRef: itemRef,
            isSelected,
            isHighlighted,
          })}
        </div>
      }
    >
      {renderContextMenuItems({ options })}
    </ContextMenu>
  );
};

// biome-ignore lint/plugin: memo erases generic params; typeof cast restores inference
export const GalleryCard = memo(_GalleryCard) as typeof _GalleryCard;
